import { makeAutoObservable, runInAction } from 'mobx';
import apiService from './services/api';

export interface User {
  id: string;
  first_name: string;
  first_name_ar: string;
  last_name: string;
  last_name_ar: string;
  email: string;
  phone?: string;
  bio?: string;
  address?: string;
  date_of_birth?: string;
  gender?: string;
  image_path?: string;
  photo?: string;
  type: string;
  is_approve?: boolean;
  account_status: string;
  service?: {
    id: string;
    name: string;
    is_approved: boolean;
  };
  created_at?: string;
  updated_at?: string;
}

export interface Category {
  id: string;
  name: string;
  name_ar?: string;
  name_de?: string;
  parent_id?: string;
  is_active: boolean;
  num_of_services?: number;
  num_of_children?: number;
  icon_path?: string;
  icon?: {
    emoji?: string;
    unicode?: string;
    type?: string;
    custom_path?: string;
  };
  children?: Category[];
  created_at: string;
  updated_at: string;
}

export interface Country {
  id: string;
  name: string;
  name_ar?: string;
  name_de?: string;
  code: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface City {
  id: string;
  name: string;
  name_ar?: string;
  name_de?: string;
  country_id: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

export interface Package {
  id: string;
  name: string;
  name_ar?: string;
  name_de?: string;
  description: string;
  description_ar?: string;
  description_de?: string;
  type: 'subscription' | 'advertisement' | 'premium';
  category?: 'subscription' | 'advertisement_with_image' | 'advertisement_without_image' | 'premium';
  price: number;
  currency: string;
  duration: number; // months for subscriptions/premium
  duration_days?: number; // days for advertisements
  is_active: boolean;
  is_default: boolean;
  is_featured: boolean;
  icon_path?: string;
  icon_url?: string;
  created_at: string;
  updated_at: string;
}


class Store {
  // Auth state
  isAuthenticated: boolean = false;
  currentUser: User | null = null;
  token: string | null = null;

  // Data state
  users: User[] = [];
  categories: Category[] = [];
  countries: Country[] = [];
  cities: City[] = [];
  packages: Package[] = [];
  subscriptions: any[] = [];

  // Loading states
  isLoading: boolean = false;
  error: string | null = null;

  // UI state
  refreshUsers: boolean = true;
  refreshCategories: boolean = true;
  refreshCountries: boolean = true;
  refreshCities: boolean = true;
  refreshPackages: boolean = true;

  // Legacy refresh flags for compatibility with existing components
  refreshGetPayments: boolean = true;
  refreshGetSales: boolean = true;
  refreshGetDepts: boolean = true;

  // Legacy fetch toggles used by admin pages
  getAllPaymentType: boolean = false;
  getAllSaleType: boolean = false;

    constructor() {
    makeAutoObservable(this);
    this.initializeAuth();
  }

  // Auth methods
  initializeAuth() {
    // تحقق من localStorage مباشرة
    const token = localStorage.getItem('admin_token');
    console.log('Initializing auth, token found:', !!token);
    if (token) {
      this.token = token;
      this.isAuthenticated = true;
      // تحديث API service بالتوكن
      apiService.setToken(token);
      console.log('User authenticated with token');
    } else {
      console.log('No token found, user not authenticated');
    }
  }

  async signIn(email: string, password: string) {
    try {
      this.setLoading(true);
      const response = await apiService.signIn(email, password);

      if (response.success) {
        const data = response.data?.user ?? response.data;
        const token = response.data?.token ?? response.data?.access_token ?? null;
        
        runInAction(() => {
          this.isAuthenticated = true;
          this.token = token;
          this.currentUser = {
            id: String(data.id ?? data.user_id ?? ''),
            first_name: data.first_name || 'Admin',
            first_name_ar: data.first_name_ar || 'مدير',
            last_name: data.last_name || 'User',
            last_name_ar: data.last_name_ar || 'مستخدم',
            email: data.email || email,
            type: data.type || 'admin',
            is_approve: data.is_approve || true,
            account_status: data.account_status || 'active',
            created_at: data.created_at || new Date().toISOString(),
            updated_at: data.updated_at || new Date().toISOString()
          };
        });
        
        // حفظ التوكن في API service
        if (token) {
          apiService.setToken(token);
        }
      }
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || (error.message ?? 'Login failed');
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async signUp(email: string, password: string, name: string) {
    try {
      this.setLoading(true);
      const response = await apiService.signUp(email, password, name);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Registration failed';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async logout() {
    console.log('Store: Starting logout...');
    try {
      await apiService.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      runInAction(() => {
        this.isAuthenticated = false;
        this.currentUser = null;
        this.token = null;
        apiService.clearToken();
      });
      
      // مسح جميع البيانات المحلية
      localStorage.removeItem('admin_token');
      localStorage.removeItem('rememberedEmail');
      localStorage.removeItem('rememberedPassword');
      localStorage.removeItem('rememberMe');
      
      // مسح sessionStorage أيضاً
      sessionStorage.clear();
      
      console.log('Store: Logout completed, all data cleared');
      
      // إعادة توجيه المستخدم إلى صفحة تسجيل الدخول
      console.log('Store: Redirecting to login page...');
      
      // إعادة توجيه فورية مع تأكيد إضافي
      console.log('Store: Immediate redirect attempt...');
      globalThis.location.href = '/login';
      
      // تأكيد إضافي بعد فترة قصيرة
      setTimeout(() => {
        console.log('Store: Secondary redirect attempt...');
        globalThis.location.replace('/login');
      }, 500);
    }
  }

  // Data fetching methods
  async fetchUsers() {
    try {
      this.setLoading(true);
      console.log('Fetching users...', new Date().toISOString());
      const response = await apiService.getProfiles();
      console.log('Users response:', response);
      console.log('Users response success:', response.success);
      console.log('Users response data:', response.data);
      
      if (response.success && response.data) {
        runInAction(() => {
          this.users = Array.isArray(response.data) ? response.data : [];
        });
        console.log('Users loaded:', this.users.length);
        console.log('Users data:', this.users);
        console.log('Users array after save:', Array.isArray(this.users), this.users.length);
        console.log('Users slice:', this.users.slice());
      } else {
        console.log('Users fetch failed or no data:', response);
      }
    } catch (error: any) {
      console.error('Error fetching users:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch users';
      });
    } finally {
      this.setLoading(false);
    }
  }

  async fetchCategories() {
    try {
      this.setLoading(true);
      console.log('Fetching categories...', new Date().toISOString());
      // Force fresh data by adding timestamp to prevent caching
      const response = await apiService.getCategories();
      console.log('Categories response:', response);
      console.log('Categories response success:', response.success);
      console.log('Categories response data:', response.data);
      
      if (response.success && response.data) {
        // Process categories with proper hierarchy
        const processCategories = (categories: any[]): Category[] => {
          return categories.map(category => ({
            id: String(category.id),
            name: category.name,
            name_ar: category.name_ar,
            name_de: category.name_de,
            parent_id: category.parent_id ? String(category.parent_id) : undefined,
            is_active: category.is_active,
            icon_path: category.icon_path,
            icon: category.icon ? {
              emoji: category.icon.emoji,
              unicode: category.icon.unicode,
              type: category.icon.type,
              custom_path: category.icon.custom_path
            } : undefined,
            created_at: category.created_at,
            updated_at: category.updated_at,
            children: category.children ? processCategories(category.children) : [],
            num_of_services: 0,
            num_of_children: category.children?.length || 0
          }));
        };
        
        const processedCategories = processCategories(response.data);
        
        runInAction(() => {
          this.categories = processedCategories;
        });
        
        console.log('Categories loaded:', this.categories.length);
        console.log('Main categories:', this.categories.filter(cat => !cat.parent_id).length);
        
      } else {
        console.log('Categories fetch failed or no data:', response);
      }
    } catch (error: any) {
      console.error('Error fetching categories:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch categories';
      });
    } finally {
      this.setLoading(false);
    }
  }

  async fetchCountries() {
    try {
      this.setLoading(true);
      console.log('Store: Fetching countries...');
      const response = await apiService.getCountries();
      console.log('Store: Countries response:', response);
      console.log('Store: Countries response.data:', response.data);
      console.log('Store: Countries response.data structure:', JSON.stringify(response.data, null, 2));
      
      if (response.success) {
        runInAction(() => {
          this.countries = response.data;
          console.log('Store: Countries set to:', this.countries);
        });
      } else {
        console.log('Store: Countries response not successful:', response);
        // Try to get data from response.data directly
        if (response.data && (Array.isArray(response.data) || response.data.data)) {
          const countriesData = Array.isArray(response.data) ? response.data : response.data.data;
          runInAction(() => {
            this.countries = countriesData;
            console.log('Store: Countries set from data directly:', this.countries);
          });
        }
      }
    } catch (error: any) {
      console.log('Store: Countries fetch error:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch countries';
      });
    } finally {
      this.setLoading(false);
    }
  }

  async fetchCities() {
    try {
      this.setLoading(true);
      console.log('Store: Fetching cities...');
      const response = await apiService.getCities();
      console.log('Store: Cities response:', response);
      console.log('Store: Cities response.data:', response.data);
      console.log('Store: Cities response.data structure:', JSON.stringify(response.data, null, 2));
      
      if (response.success) {
        runInAction(() => {
          this.cities = response.data;
          console.log('Store: Cities set to:', this.cities);
        });
      } else {
        console.log('Store: Cities response not successful:', response);
        // Try to get data from response.data directly
        if (response.data && (Array.isArray(response.data) || response.data.data)) {
          const citiesData = Array.isArray(response.data) ? response.data : response.data.data;
          runInAction(() => {
            this.cities = citiesData;
            console.log('Store: Cities set from data directly:', this.cities);
          });
        }
      }
    } catch (error: any) {
      console.log('Store: Cities fetch error:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch cities';
      });
    } finally {
      this.setLoading(false);
    }
  }

  async createCountry(countryData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.createCountry(countryData);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to create country';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async updateCountry(countryId: string, countryData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.updateCountry(countryId, countryData);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update country';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async deleteCountry(countryId: string) {
    try {
      this.setLoading(true);
      const response = await apiService.deleteCountry(countryId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to delete country';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async createCity(cityData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.createCity(cityData);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to create city';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async updateCity(cityId: string, cityData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.updateCity(cityId, cityData);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update city';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async deleteCity(cityId: string) {
    try {
      this.setLoading(true);
      const response = await apiService.deleteCity(cityId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to delete city';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async fetchPackages() {
    try {
      this.setLoading(true);
      console.log('Fetching packages...', new Date().toISOString());
      const response = await apiService.getPackages();
      console.log('Packages response:', response);
      console.log('Packages response success:', response.success);
      console.log('Packages response data:', response.data);
      
      if (response.success && response.data) {
        runInAction(() => {
          this.packages = Array.isArray(response.data) ? response.data : [];
        });
        console.log('Packages loaded:', this.packages.length);
        console.log('Packages data:', this.packages);
        console.log('Packages array after save:', Array.isArray(this.packages), this.packages.length);
        console.log('Packages slice:', this.packages.slice());
      } else {
        console.log('Packages fetch failed or no data:', response);
      }
    } catch (error: any) {
      console.error('Error fetching packages:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch packages';
      });
    } finally {
      this.setLoading(false);
    }
  }


  async fetchSubscriptions() {
    try {
      this.setLoading(true);
      const response = await apiService.getSubscriptions();
      if (response.success) {
        runInAction(() => {
          this.subscriptions = response.data;
        });
      }
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch subscriptions';
      });
    } finally {
      this.setLoading(false);
    }
  }

  // Subscription management methods
  async getAllSubscriptions(filters: any = {}) {
    try {
      this.setLoading(true);
      const response = await apiService.getAllSubscriptions(filters);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch subscriptions';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async getSubscriptionStatistics() {
    try {
      this.setLoading(true);
      const response = await apiService.getSubscriptionStatistics();
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch subscription statistics';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async getSubscriptionDetails(id: string) {
    try {
      this.setLoading(true);
      const response = await apiService.getSubscriptionDetails(id);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch subscription details';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async createSubscription(subscriptionData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.createSubscription(subscriptionData);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to create subscription';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async updateSubscriptionStatus(id: string, statusData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.updateSubscriptionStatus(id, statusData);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update subscription status';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async deleteSubscription(id: string) {
    try {
      this.setLoading(true);
      const response = await apiService.deleteSubscription(id);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to delete subscription';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // User management methods
  async approveAccount(userId: string) {
    try {
      const response = await apiService.approveAccount(userId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to approve account';
      });
      throw error;
    }
  }

  async blockProfile(userId: string) {
    try {
      const response = await apiService.blockProfile(userId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to block profile';
      });
      throw error;
    }
  }

  async unblockProfile(userId: string) {
    try {
      const response = await apiService.unblockProfile(userId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to unblock profile';
      });
      throw error;
    }
  }

  async updateUser(userId: string, userData: any) {
    try {
      this.setLoading(true);
      console.log('Store: Updating user:', userId, userData);
      const response = await apiService.updateProfile(userId, userData);
      console.log('Store: Update response:', response);
      if (response.success) {
        console.log('Store: Update successful, refreshing users...');
        await this.fetchUsers(); // Refresh the list
        console.log('Store: Users refreshed after update');
      }
      return response;
    } catch (error: any) {
      console.error('Store: Update error:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update user';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async deleteUser(userId: string) {
    try {
      this.setLoading(true);
      console.log('Store: Deleting user:', userId);
      const response = await apiService.deleteProfile(userId);
      console.log('Store: Delete response:', response);
      if (response.success) {
        console.log('Store: Delete successful, refreshing users...');
        await this.fetchUsers(); // Refresh the list
        console.log('Store: Users refreshed after delete');
      }
      return response;
    } catch (error: any) {
      console.error('Store: Delete error:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to delete user';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // Category management methods
  async createCategory(categoryData: any) {
    try {
      this.setLoading(true);
      console.log('Store: Creating category:', categoryData);
      const response = await apiService.createCategory(categoryData);
      console.log('Store: Create category response:', response);
      if (response.success) {
        console.log('Store: Category created successfully, refreshing categories...');
        await this.fetchCategories();
        console.log('Store: Categories refreshed after create');
      }
      return response;
    } catch (error: any) {
      console.error('Store: Create category error:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to create category';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async updateCategory(categoryId: string, categoryData: any) {
    try {
      this.setLoading(true);
      console.log('Store: Updating category:', categoryId, categoryData);
      const response = await apiService.updateCategory(categoryId, categoryData);
      console.log('Store: Update category response:', response);
      if (response.success) {
        console.log('Store: Category updated successfully, refreshing categories...');
        await this.fetchCategories();
        console.log('Store: Categories refreshed after update');
      }
      return response;
    } catch (error: any) {
      console.error('Store: Update category error:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update category';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async deleteCategory(categoryId: string) {
    try {
      this.setLoading(true);
      console.log('Store: Deleting category:', categoryId);
      const response = await apiService.deleteCategory(categoryId);
      console.log('Store: Delete category response:', response);
      if (response.success) {
        console.log('Store: Category deleted successfully, refreshing categories...');
        await this.fetchCategories();
        console.log('Store: Categories refreshed after delete');
      }
      return response;
    } catch (error: any) {
      console.error('Store: Delete category error:', error);
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to delete category';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  // Package management methods
  async createPackage(packageData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.createPackage(packageData);
      this.refreshPackages = true;
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to create package';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async updatePackage(packageId: string, packageData: any) {
    try {
      this.setLoading(true);
      const response = await apiService.updatePackage(packageId, packageData);
      this.refreshPackages = true;
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to update package';
      });
      throw error;
    } finally {
      this.setLoading(false);
    }
  }

  async deletePackage(packageId: string) {
    try {
      const response = await apiService.deletePackage(packageId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to delete package';
      });
      throw error;
    }
  }

  async togglePackageStatus(packageId: string, isActive: boolean) {
    try {
      const response = isActive 
        ? await apiService.deactivatePackage(packageId)
        : await apiService.activatePackage(packageId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to toggle package status';
      });
      throw error;
    }
  }

  async activatePackage(packageId: string) {
    try {
      const response = await apiService.activatePackage(packageId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to activate package';
      });
      throw error;
    }
  }

  async deactivatePackage(packageId: string) {
    try {
      const response = await apiService.deactivatePackage(packageId);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to deactivate package';
      });
      throw error;
    }
  }


  // Utility methods
  setLoading(loading: boolean) {
    this.isLoading = loading;
  }

  setError(error: string | null) {
    this.error = error;
  }

  clearError() {
    this.error = null;
  }

  // Toggle refresh methods for real-time updates
  toggleRefreshUsers() {
    this.refreshUsers = !this.refreshUsers;
  }

  toggleRefreshCategories() {
    this.refreshCategories = !this.refreshCategories;
  }

  toggleRefreshCountries() {
    this.refreshCountries = !this.refreshCountries;
  }

  toggleRefreshCities() {
    this.refreshCities = !this.refreshCities;
  }

  toggleRefreshPackages() {
    this.refreshPackages = !this.refreshPackages;
  }


  // ----- Legacy compatibility methods (used by existing admin pages) -----
  toggleRefreshPayments() {
    this.refreshGetPayments = !this.refreshGetPayments;
  }
  getRefreshPayments() {
        return this.refreshGetPayments;
    }

  toggleRefreshSales() {
    this.refreshGetSales = !this.refreshGetSales;
        }
  getRefreshSales() {
            return this.refreshGetSales;
        }

  toggleRefreshDepts() {
    this.refreshGetDepts = !this.refreshGetDepts;
        }
  getRefreshDepts() {
            return this.refreshGetDepts;
        }

  // Account Approval Requests
  async getAccountApprovals() {
    try {
      const response = await apiService.getAccountApprovals();
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch account approvals';
      });
      throw error;
    }
  }

  async approveAccountRequest(id: string, type: string, notes?: string) {
    try {
      const response = await apiService.approveAccountRequest(id, type, notes);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to approve account request';
      });
      throw error;
    }
  }

  async rejectAccountRequest(id: string, type: string, reason: string) {
    try {
      const response = await apiService.rejectAccountRequest(id, type, reason);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to reject account request';
      });
      throw error;
    }
  }

  // Payment Subscriptions
  async getPaymentSubscriptions() {
    try {
      const response = await apiService.getPaymentSubscriptions();
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch payment subscriptions';
      });
      throw error;
    }
  }

  async publishService(id: string, serviceData: any) {
    try {
      const response = await apiService.publishService(id, serviceData);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to publish service';
      });
      throw error;
    }
  }

  async getRenewalNotifications() {
    try {
      const response = await apiService.getRenewalNotifications();
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to fetch renewal notifications';
      });
      throw error;
    }
  }

  async markRenewalNotificationSent(id: string) {
    try {
      const response = await apiService.markRenewalNotificationSent(id);
      return response;
    } catch (error: any) {
      runInAction(() => {
        this.error = error.response?.data?.message || 'Failed to mark renewal notification';
      });
      throw error;
    }
  }

  // Getters for categories
  get mainCategories() {
    return this.categories.filter(cat => !cat.parent_id);
  }

  getSubcategories(parentId: string) {
    return this.categories.filter(cat => cat.parent_id === parentId);
  }

  // Get all categories flattened (for compatibility)
  get allCategories() {
    const flatten = (categories: Category[]): Category[] => {
      let result: Category[] = [];
      for (const category of categories) {
        result.push(category);
        if (category.children && category.children.length > 0) {
          result = result.concat(flatten(category.children));
        }
      }
      return result;
    };
    return flatten(this.categories);
  }
}

const store = new Store();

console.log('Store: Store instance created successfully');

// إضافة Store إلى window object للوصول إليه من LogoutButton
if (globalThis.window !== undefined) {
  (globalThis as any).store = store;
}

export default store;